import React, { ReactNode } from 'react';
import cx from 'classnames';
import { Flags } from 'react-feature-flags';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { WishlistFilled } from '@JohnLewisPartnership/jl-design-system/dist/components/icons/WishlistFilled';
import { WishlistOutlined } from '@JohnLewisPartnership/jl-design-system/dist/components/icons/WishlistOutlined';
import styles from './AddToWishListButton.module.scss';
import { FEATURE_FLAGS } from '../../helpers/featureFlags/constants';
import { redirectToSignIn } from '../../helpers/redirect/redirect';
import Cookies from 'js-cookie';
import { getExperiencesFromWindow, isFlagSet } from '../../helpers/featureFlags/featureFlags';
import { fetchMonetateExperiences, postMonetateImpressionByName } from '../../helpers/monetate/monetate';
import { GUEST_WISH_LIST_EXPERIENCE } from '../../stores/constants';
import { getExperiences } from '../../helpers/productDetails/productDetails';

export interface AddToWishListButtonProps {
    handleWishListOnClick?: (wishListId: string) => Promise<void>;
    isAdded?: boolean;
    isExpanded?: boolean;
    isSubmitting?: boolean;
    productId: string;
}

export const AddToWishListButton: React.FC<AddToWishListButtonProps> = ({
    handleWishListOnClick,
    isAdded = false,
    isExpanded = false,
    isSubmitting = false,
    productId,
}: AddToWishListButtonProps) => {
    const wishListIcon = isAdded ? <WishlistFilled /> : <WishlistOutlined />;
    const buttonText = getButtonText(isAdded);

    return (
        <SecondaryButton
            data-testid="add-to-wish-list-button"
            onClick={async (): Promise<void> => {
                const windowExperiences = getExperiencesFromWindow();
                const experiences = getExperiences();
                const headerExperiences =
                    windowExperiences.length === 0 && experiences.length === 0 ? await fetchMonetateExperiences() : [];
                const isGuest = !Cookies.get('shopperId');

                if (isGuest) {
                    postMonetateImpressionByName(GUEST_WISH_LIST_EXPERIENCE, windowExperiences || headerExperiences);
                }

                if (isGuest && !isFlagSet(FEATURE_FLAGS.GUEST_WISHLIST, experiences || headerExperiences)) {
                    // Guest user without guest wishlist feature flag
                    redirectToSignIn(productId, 'button');
                    return;
                } else {
                    return handleWishListOnClick?.('');
                }
            }}
            className={cx(styles.addToWishListButton, {
                [styles.wishListExpanded]: isExpanded,
            })}
            submitting={isSubmitting}
        >
            <div className={styles.wishListButtonContent}>
                {wishListIcon}
                <div>{buttonText}</div>
            </div>
        </SecondaryButton>
    );
};

const getButtonText = (isAdded: boolean): JSX.Element => (
    <Flags
        authorizedFlags={[FEATURE_FLAGS.NURSERY_PROTOTYPE]}
        renderOn={(): ReactNode => <>{isAdded ? 'Added to baby wish list' : 'Add to baby wish list'}</>}
        renderOff={(): ReactNode => <>{isAdded ? 'Added to wish list' : 'Add to wish list'}</>}
    />
);

export default AddToWishListButton;
